import { Alert, Button } from '@mui/material'
import { useReplayMigrationEvents } from '@src/api/loan/loan-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { REPLAY_MIGRATION_COUNTDOWN_SECONDS } from '@src/types/constants'
import { SnackbarContent, useSnackbar, type SnackbarKey } from 'notistack'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ReplayMigrationEventsSnackbarProps {
  snackbarKey: SnackbarKey
  loanId: string
  topOffsetPx?: number
  onClose: () => void
}

const ReplayMigrationEventsSnackbar = forwardRef<HTMLDivElement, ReplayMigrationEventsSnackbarProps>(
  ({ snackbarKey, loanId, topOffsetPx = 0, onClose }, ref) => {
    const { t } = useTranslation()
    const { closeSnackbar } = useSnackbar()
    const { showSuccess, showError } = useNotification()
    const replayMutation = useReplayMigrationEvents(loanId)

    const [secondsLeft, setSecondsLeft] = useState(REPLAY_MIGRATION_COUNTDOWN_SECONDS)
    const cancelledRef = useRef(false)
    const closedRef = useRef(false)

    // The countdown effect must only schedule the interval/timeout once on
    // mount. Capture every closure it needs in a ref that we refresh on each
    // render — this lets us keep an empty dependency array without disabling
    // the exhaustive-deps rule.
    const latestRef = useRef({ closeSnackbar, snackbarKey, replayMutation, showSuccess, showError, t, onClose })
    latestRef.current = { closeSnackbar, snackbarKey, replayMutation, showSuccess, showError, t, onClose }

    const notifyClosedOnce = () => {
      if (closedRef.current) return
      closedRef.current = true
      latestRef.current.onClose()
    }

    useEffect(() => {
      const intervalId = setInterval(() => {
        setSecondsLeft((prev) => Math.max(0, prev - 1))
      }, 1000)

      const timeoutId = setTimeout(() => {
        clearInterval(intervalId)
        const latest = latestRef.current
        latest.closeSnackbar(latest.snackbarKey)

        if (cancelledRef.current) {
          notifyClosedOnce()
          return
        }

        latest.replayMutation.mutate(undefined, {
          onSuccess: () => {
            latest.showSuccess(latest.t('loan.payment.replayMigrationEvents.success'))
            notifyClosedOnce()
          },
          onError: () => {
            latest.showError(latest.t('loan.payment.replayMigrationEvents.error'))
            notifyClosedOnce()
          },
        })
      }, REPLAY_MIGRATION_COUNTDOWN_SECONDS * 1000)

      return () => {
        clearInterval(intervalId)
        clearTimeout(timeoutId)
        notifyClosedOnce()
      }

    }, [])

    const handleCancel = () => {
      cancelledRef.current = true
      closeSnackbar(snackbarKey)
      notifyClosedOnce()
    }

    return (
      <SnackbarContent ref={ref} style={{ marginTop: topOffsetPx }}>
        <Alert
          severity="error"
          variant="filled"
          action={
            <Button color="inherit" size="small" onClick={handleCancel}>
              {t('loan.payment.replayMigrationEvents.cancel')}
            </Button>
          }
        >
          {t('loan.payment.replayMigrationEvents.pending', { count: secondsLeft })}
        </Alert>
      </SnackbarContent>
    )
  },
)

ReplayMigrationEventsSnackbar.displayName = 'ReplayMigrationEventsSnackbar'

export default ReplayMigrationEventsSnackbar
